import { ColTypeDef, ICellRendererParams } from "ag-grid-community";
import { format, isValid, parseISO } from "date-fns";
import React from "react";

import { Skeleton } from "../../../atoms/Skeleton";
import { Text } from "../../../atoms/Text";

// Custom date cell renderer that supports format override
export const DateCellRenderer = (params: ICellRendererParams) => {
	// Check if this is a loading/empty row (same logic as Table's cellRendererSelector)
	if (params.data && Object.keys(params.data).length === 0) {
		return (
			<div className="flex size-full min-h-0 items-center justify-start">
				<Skeleton />
			</div>
		);
	}

	const { value, valueFormatted } = params;

	// Use valueFormatted if available (from valueFormatter), otherwise use value
	const displayValue = valueFormatted ?? value ?? "";

	return (
		<div className="flex size-full min-h-0 items-center justify-start">
			<div className="py-line-clamp-1">
				<Text size="paragraph-3" weight="regular">
					{displayValue}
				</Text>
			</div>
		</div>
	);
};

const DateCol: ColTypeDef = {
	cellRenderer: DateCellRenderer,
	valueFormatter: (params) => {
		const { value, colDef } = params;
		const dateFormat = colDef.cellRendererParams?.dateFormat || "MMM dd, yyyy";

		// Handle loading/empty rows or missing values without logging warnings
		const noData = value === null || value === undefined;
		const isLoading = params.data && Object.keys(params.data).length === 0;
		if (noData || isLoading) {
			isLoading && console.warn("DateCol: params.data is empty", value, params.data);
			return "";
		}

		try {
			const date = typeof value === "string" ? parseISO(value) : new Date(value);
			if (!isValid(date)) {
				return "";
			}
			return format(date, dateFormat);
		} catch {
			return "";
		}
	},
	// Add a comparator for proper date sorting
	comparator: (valueA, valueB) => {
		try {
			const dateA = typeof valueA === "string" ? parseISO(valueA) : new Date(valueA);
			const dateB = typeof valueB === "string" ? parseISO(valueB) : new Date(valueB);

			if (isValid(dateA) && isValid(dateB)) {
				return dateA.getTime() - dateB.getTime();
			}

			// If one or both dates are invalid, sort invalid dates to the end
			if (!isValid(dateA) && !isValid(dateB)) return 0;
			if (!isValid(dateA)) return 1;
			if (!isValid(dateB)) return -1;

			return 0;
		} catch (error) {
			return 0;
		}
	},
};

export default DateCol;
